import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import axiosInstant from "../../../../../api";
import { listProductPdpUrls, listProductPdpUrlScores } from "../../../../../api/endpoints";
import { IVideo } from "../../hooks/useFetchVideos";
import {
  isAttemptPending,
  usePendingScorePoll,
} from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlAttempt, PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlScorePoint } from "../../../../../types/rmn";
import type { PanelStatus } from "../panels";
import { pageAssignment } from "../pdpAssignment";

/**
 * The PDP page score for a video — data layer.
 *
 * WHY THIS PANEL EXISTS: analytics only accrue once a video is embedded, put on
 * a landing page or QR-scanned, so the plays chart is empty for the overwhelming
 * majority of the library — measured against a real dataset, 59 of 3029 videos
 * had a single play, while 813 reached a scorable product page. The two barely
 * overlap (20 videos had both).
 *
 * WHAT IT IS: every PDP URL carries three 0–100 scores from a monthly LLM audit
 * of the page — Overall, Human Conversion and Agentic Commerce. It belongs to a
 * (product, brand) pair, not to a video, which is why this resolves through
 * `videoProducts`.
 */

export interface PdpUrlRow {
  id: number;
  pdp_url: string;
  /** The product the row belongs to — the rescore route is addressed by it. */
  desireid?: number | null;
  /** Our videos on the page (one link each) — a page can carry several. */
  videos?: { s3_file_id: number }[] | null;
  retailer?: string | null;
  /** Deactivated rows still list and count, but the cron no longer scores them. */
  active?: boolean | null;
  latest_score?: PdpUrlScore | null;
  latest_attempt?: PdpUrlAttempt | null;
}

export interface PageScoreState {
  status: PanelStatus;
  /** The row the panel speaks for. */
  row: PdpUrlRow | null;
  /** Every row this video reaches, ranked. */
  rows: PdpUrlRow[];
  /** Rows with a score, the headline first. */
  scoredRows: PdpUrlRow[];
  /**
   * The headline row's ready scores, NEWEST FIRST — `[0]` is `latest_score`.
   * `null` until loaded, or when the load failed; the panel then says nothing
   * about movement rather than mislabelling the score as a first score.
   */
  history: PdpUrlScore[] | null;
  /** Hard reload — shows the loading state. */
  reload: () => void;
  /** Silent refresh — swaps rows in place. What the poll calls. */
  refresh: () => Promise<void>;
}

/** "https://www.samsclub.com/ip/OIKOS..." -> "samsclub.com". */
export const urlHost = (raw?: string | null): string => {
  if (!raw) return "";
  try {
    return new URL(raw).hostname.replace(/^www\./, "");
  } catch {
    return String(raw).replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }
};

/** "https://www.myrevair.com/products/revair?x=1" -> "myrevair.com/products/revair". */
export const urlHostPath = (raw?: string | null): string => {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    return `${u.hostname.replace(/^www\./, "")}${u.pathname.replace(/\/$/, "")}`;
  } catch {
    return String(raw).replace(/^https?:\/\//, "").replace(/^www\./, "").split("?")[0];
  }
};

export const hasScore = (row: PdpUrlRow) => row.latest_score?.overall_score != null;

const scoredAtMs = (row: PdpUrlRow) => {
  const at = row.latest_score?.scored_at;
  return at ? new Date(at).getTime() : 0;
};

/**
 * The row the panel speaks for: carries this video → has a score → most
 * recently scored. `is_primary` used to make this choice and was dropped in
 * August. NOTE the consequence the handoff calls out: the link outranks "has a
 * score", so an unscored linked row can suppress a scored sibling — which is
 * why the page count renders in the Not-scored state too. (Backend ticket:
 * rank scored rows above linked ones.)
 */
export const rankRows = (rows: PdpUrlRow[], videoId?: number): PdpUrlRow[] =>
  [...rows].sort((a, b) => {
    // Through pageAssignment, not a second inlined copy of the rule: the modal's
    // chips and this panel's strip both read ownership from that one function, so
    // the row this picks and the row the strip describes cannot diverge.
    const own = Number(pageAssignment(b, videoId) === "mine") - Number(pageAssignment(a, videoId) === "mine");
    if (own !== 0) return own;
    const scored = Number(hasScore(b)) - Number(hasScore(a));
    if (scored !== 0) return scored;
    return scoredAtMs(b) - scoredAtMs(a);
  });

/**
 * The headline row's score-over-time series, from the endpoint TDC-1499 added
 * for exactly this — `GET .../pdp-urls/:id/scores`: ready runs only,
 * chronological, no report blobs. It is the same source the product settings
 * panel's PdpScoreHistory chart reads, so the two surfaces can never disagree.
 *
 * Held newest-first because every derivation below reads `[0]` as "now" and
 * `[1]` as "previous". Refetches when the row's latest ready score changes —
 * a rescore just landed — the same trigger PdpScoreHistory uses.
 */
const useScoreHistory = (row: PdpUrlRow | null, fallbackProductId: number | null): PdpUrlScore[] | null => {
  const [history, setHistory] = useState<PdpUrlScore[] | null>(null);
  const loadedRowId = useRef<number | null>(null);

  const rowId = row?.id ?? null;
  const productId = row?.desireid ?? fallbackProductId;
  const latestScoreId = row?.latest_score?.id ?? null;

  useEffect(() => {
    if (!rowId || !productId) {
      loadedRowId.current = null;
      setHistory(null);
      return undefined;
    }
    // A different row: drop the old row's series rather than show it under the
    // new headline while the request is out. A new score on the SAME row keeps
    // the old series on screen until the fresh one replaces it.
    if (loadedRowId.current !== rowId) {
      loadedRowId.current = rowId;
      setHistory(null);
    }
    let live = true;
    axiosInstant
      .get(listProductPdpUrlScores(productId, rowId))
      .then(({ data }) => {
        if (!live) return;
        const points = (Array.isArray(data) ? data : data?.data || []) as PdpUrlScorePoint[];
        setHistory(
          points
            .filter((p) => p.scored_at)
            .map((p) => ({
              id: p.id,
              overall_score: p.overall_score,
              human_conversion_score: p.human_conversion_score,
              agentic_commerce_score: p.agentic_commerce_score,
              scored_at: p.scored_at,
            }))
            .reverse(),
        );
      })
      .catch(() => {
        if (live) setHistory(null);
      });
    return () => {
      live = false;
    };
  }, [rowId, productId, latestScoreId]);

  return history;
};

export const usePageScore = (video: IVideo, brandId?: number, reloadKey = 0): PageScoreState => {
  const [rows, setRows] = useState<PdpUrlRow[]>([]);
  const [status, setStatus] = useState<PanelStatus>("loading");
  const [attempt, setAttempt] = useState(0);
  // Guards against a slow response landing after a newer request.
  const seq = useRef(0);

  const videoId = video?.id;
  const productIds = (video?.videoProducts || [])
    .map((p: any) => Number(p?.desireid))
    .filter((n: number) => Number.isFinite(n) && n > 0);
  const productKey = productIds.join(",");

  const load = useCallback(
    async (silent: boolean) => {
      const ids = productKey ? productKey.split(",").map(Number) : [];
      if (!brandId || ids.length === 0) {
        setRows([]);
        setStatus("ready");
        return;
      }
      seq.current += 1;
      const mine = seq.current;
      if (!silent) setStatus("loading");
      try {
        const lists = await Promise.all(
          ids.map((id) =>
            axiosInstant
              .get(listProductPdpUrls(id, brandId))
              .then(({ data }) => (Array.isArray(data) ? data : data?.data || [])),
          ),
        );
        if (mine !== seq.current) return;
        // EVERY page for this video's products, deliberately NOT the settings
        // modal's narrower scope. That modal filters to rows this video is
        // stamped on because it is a WRITE surface; a score describes a product
        // page, and every video for that product is a video for that page.
        setRows(lists.flat());
        setStatus("ready");
      } catch {
        if (mine !== seq.current) return;
        if (!silent) {
          setRows([]);
          setStatus("error");
        }
        // A silent refresh that fails keeps the rows it had; the next poll retries.
      }
    },
    [brandId, productKey],
  );

  useEffect(() => {
    load(false);
  }, [load, attempt, videoId]);

  // The host page bumps `reloadKey` when the settings modal closes: its Product
  // Pages tab is where a page gets assigned to this video, and the modal only
  // reports General-tab saves. Refresh IN PLACE — rows swap, no skeleton — and
  // the history hook below re-points itself if the headline row moved. The ref
  // makes this fire on a changed key only, never on mount or on `load` churn.
  const seenReloadKey = useRef(reloadKey);
  useEffect(() => {
    if (seenReloadKey.current === reloadKey) return;
    seenReloadKey.current = reloadKey;
    load(true);
  }, [reloadKey, load]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const refresh = useCallback(() => load(true), [load]);

  // The visible contract of the pending state: "Checking every 5 seconds".
  // Polls only while an attempt is live, stops on resolve, and stops after the
  // 15-minute staleness bound — at which point the panel renders the stalled
  // copy instead of promising a result forever.
  usePendingScorePoll(rows, refresh);

  const ranked = rankRows(rows, videoId);
  const head = ranked[0] || null;
  // Rows carry their own `desireid`; the single-product case is the only safe
  // fallback if a row ever arrives without it.
  const history = useScoreHistory(head, productIds.length === 1 ? productIds[0] : null);

  return {
    status,
    row: head,
    rows: ranked,
    scoredRows: ranked.filter(hasScore),
    history,
    reload,
    refresh,
  };
};

// ---------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------

/** Four or more points earn a trend; below that the track shows movement. */
export const TREND_MIN_POINTS = 4;

/**
 * A word per band, on the 70/40 banding that already ships — a label, not a
 * fourth scale. Plain and evaluative rather than euphemistic: the number is a
 * judgement, and dressing it up would mislead.
 */
export const bandWord = (score: number | null | undefined): string => {
  if (score == null) return "Not scored";
  if (score >= 70) return "Strong";
  if (score >= 40) return "Needs work";
  return "Weak";
};

/**
 * The month-label rule (§10.2). A manual rescore bypasses the monthly guard, so
 * two scores can share a calendar month and "since September" on a September
 * score reads as nonsense. Different months → name the month; same month →
 * print the date.
 */
export const sinceLabel = (nowAt?: string | null, prevAt?: string | null): { label: string; sameMonth: boolean } => {
  if (!prevAt) return { label: "", sameMonth: false };
  const now = dayjs(nowAt || undefined);
  const prev = dayjs(prevAt);
  const sameMonth = now.isSame(prev, "month");
  return { label: sameMonth ? prev.format("D MMM") : prev.format("MMMM"), sameMonth };
};

export type Movement =
  | { kind: "first" }
  | { kind: "zero"; since: string }
  | { kind: "delta"; change: number; from: number; since: string; sameMonth: boolean };

/** The four movement forms — all four are the Scored state. */
export const movement = (history?: PdpUrlScore[] | null): Movement => {
  if (!history || history.length < 2) return { kind: "first" };
  const [now, prev] = history;
  if (now?.overall_score == null || prev?.overall_score == null) return { kind: "first" };
  const change = now.overall_score - prev.overall_score;
  const { label, sameMonth } = sinceLabel(now.scored_at, prev.scored_at);
  if (change === 0) return { kind: "zero", since: label };
  return { kind: "delta", change, from: prev.overall_score, since: label, sameMonth };
};

type SubKey = "human_conversion_score" | "agentic_commerce_score";

export const subDelta = (history: PdpUrlScore[] | null | undefined, key: SubKey): number | null => {
  if (!history || history.length < 2) return null;
  const a = history[0]?.[key];
  const b = history[1]?.[key];
  if (a == null || b == null) return null;
  return a - b;
};

export const subPrevious = (history: PdpUrlScore[] | null | undefined, key: SubKey): number | null => {
  if (!history || history.length < 2) return null;
  return history[1]?.[key] ?? null;
};

/** Oldest-first points for the trend; ready scores only. */
export const trendPoints = (history?: PdpUrlScore[] | null): Array<{ date: string; value: number }> =>
  (history || [])
    .filter((h) => h.overall_score != null && h.scored_at)
    .map((h) => ({ date: h.scored_at as string, value: h.overall_score as number }))
    .reverse();

export const scoredDate = (at?: string | null): string => (at ? dayjs(at).format("D MMM YYYY") : "—");
export const scoredMonth = (at?: string | null): string => (at ? dayjs(at).format("MMM YYYY") : "—");
export const nextScoreMonth = (at?: string | null): string => (at ? dayjs(at).add(1, "month").format("MMMM") : "");
export const monthsOutOfDate = (at?: string | null): number => (at ? Math.max(0, dayjs().diff(dayjs(at), "month")) : 0);
export const minutesSince = (at?: string | null): number => (at ? Math.max(0, dayjs().diff(dayjs(at), "minute")) : 0);

/** Pending for longer than the 15-minute bound: treated as dead, falls through to Not scored. */
export const isStalled = (attempt?: PdpUrlAttempt | null): boolean =>
  !!attempt && attempt.status === "pending" && !isAttemptPending(attempt);

/** A failed attempt that is newer than the score on show — the score is real, just old. */
export const failedSinceScore = (score?: PdpUrlScore | null, attempt?: PdpUrlAttempt | null): boolean =>
  !!attempt &&
  attempt.status === "error" &&
  (!score ||
    !score.scored_at ||
    !attempt.created_at ||
    new Date(attempt.created_at).getTime() > new Date(score.scored_at).getTime());

/**
 * Known tokens get human copy; anything else is an unbounded string and gets
 * the mono block. The raw token always prints small so support can quote it.
 */
export const humanReason = (token?: string | null): { text: string; known: boolean } => {
  switch (token) {
    case "FetchFailed":
      return { text: "Couldn't fetch the page", known: true };
    case "AiQuotaExhausted":
      return { text: "The audit service is out of capacity", known: true };
    case "ScraperOutOfCredits":
      return { text: "Page fetching is out of credits. An admin needs to top this up.", known: true };
    case "ScoreAlreadyInProgress":
      return { text: "A report is already running for this page", known: true };
    case "RateLimited":
      return { text: "You've reached the hourly limit for reports. Try again later.", known: true };
    default:
      return { text: token || "Unknown error", known: false };
  }
};
